import React, { useEffect, useState } from "react";
import { Search, Ban, CheckCircle, Gift, History } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/i18n/I18nContext";
import api from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

const QUICK_AMOUNTS = [10, 20, 50, 100, 200];
// Only "spend" (song generation) and "refund_reversal" (a refunded purchase taking its
// tokens back) actually remove tokens - every other type (purchase, manual_credit,
// welcome_bonus, referral_bonus, refund) adds tokens and must render as a positive "+".
const NEGATIVE_TX_TYPES = new Set(["spend", "refund_reversal"]);

export default function UsersTab() {
  const { t } = useI18n();
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [creditTarget, setCreditTarget] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [history, setHistory] = useState(null);

  const load = () => api.get("/admin/users", { params: { search: search || undefined } }).then(({ data }) => setUsers(data));

  useEffect(() => { load(); }, [search]);

  const toggleBlock = async (u) => {
    await api.patch(`/admin/users/${u.user_id}`, { is_blocked: !u.is_blocked });
    load();
  };

  const openCredit = (u) => {
    setCreditTarget(u);
    setCustomAmount("");
    setReason("");
  };

  const submitCredit = async (amount) => {
    const finalAmount = amount || parseFloat(customAmount);
    if (!finalAmount || finalAmount <= 0) {
      toast({ variant: "destructive", title: t("admin.creditAmountRequired") });
      return;
    }
    if (!reason.trim()) {
      toast({ variant: "destructive", title: t("admin.creditReasonRequired") });
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/admin/users/${creditTarget.user_id}/credit-tokens`, { amount: finalAmount, reason });
      toast({ title: `+${finalAmount} ${t("admin.tokens")} - ${t("admin.creditSuccess")}` });
      setCreditTarget(null);
      load();
    } catch (err) {
      toast({ variant: "destructive", title: t("common.error"), description: err?.response?.data?.detail });
    } finally {
      setSubmitting(false);
    }
  };

  const openHistory = async (u) => {
    setHistoryTarget(u);
    setHistory(null);
    const { data } = await api.get(`/admin/users/${u.user_id}/history`);
    setHistory(data);
  };

  return (
    <div>
      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid={ADMIN.searchInput} placeholder={t("admin.searchPlaceholder")} value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-full pl-9" />
      </div>

      <div className="overflow-x-auto rounded-2xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="p-4">Email</th>
              <th className="p-4">Nom</th>
              <th className="p-4">Rôle</th>
              <th className="p-4">Jetons</th>
              <th className="p-4">Statut</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} data-testid={ADMIN.userRow(u.user_id)} className="border-t border-border">
                <td className="p-4">{u.email}</td>
                <td className="p-4">{u.name}</td>
                <td className="p-4 capitalize">{u.role}</td>
                <td className="p-4 font-semibold">{u.wallet_tokens}</td>
                <td className="p-4">
                  <Badge variant={u.is_blocked ? "destructive" : "default"} className="rounded-full">
                    {u.is_blocked ? t("admin.blockUser") : "OK"}
                  </Badge>
                </td>
                <td className="p-4">
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" className="rounded-full" data-testid={ADMIN.creditTokensBtn(u.user_id)} onClick={() => openCredit(u)}>
                      <Gift className="mr-1 h-3.5 w-3.5" />
                      {t("admin.creditTokens")}
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full" data-testid={ADMIN.viewHistoryBtn(u.user_id)} onClick={() => openHistory(u)}>
                      <History className="mr-1 h-3.5 w-3.5" />
                      {t("admin.viewHistory")}
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full" data-testid={ADMIN.blockUserBtn(u.user_id)} onClick={() => toggleBlock(u)}>
                      {u.is_blocked ? <CheckCircle className="mr-1 h-3.5 w-3.5" /> : <Ban className="mr-1 h-3.5 w-3.5" />}
                      {u.is_blocked ? t("admin.unblockUser") : t("admin.blockUser")}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!creditTarget} onOpenChange={(open) => !open && setCreditTarget(null)}>
        <DialogContent className="max-w-md" data-testid={ADMIN.creditTokensDialog}>
          {creditTarget && (
            <>
              <DialogHeader>
                <DialogTitle>{t("admin.creditTokensTitle")}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <p className="text-muted-foreground">{creditTarget.email} · {t("admin.currentBalance")}: <strong>{creditTarget.wallet_tokens}</strong></p>
                <div className="flex flex-wrap gap-2">
                  {QUICK_AMOUNTS.map((n) => (
                    <Button key={n} size="sm" variant="outline" className="rounded-full" data-testid={ADMIN.creditAmountBtn(n)} disabled={submitting} onClick={() => submitCredit(n)}>
                      +{n} {t("admin.tokens")}
                    </Button>
                  ))}
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">{t("admin.customAmount")}</label>
                  <Input
                    type="number"
                    min="1"
                    className="mt-1 rounded-xl"
                    data-testid={ADMIN.creditCustomAmountInput}
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    placeholder="ex: 35"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">{t("admin.creditReason")}</label>
                  <Textarea
                    className="mt-1 rounded-xl"
                    data-testid={ADMIN.creditReasonInput}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={t("admin.creditReasonPlaceholder")}
                    rows={2}
                  />
                </div>
                <Button className="w-full rounded-full" data-testid={ADMIN.creditSubmitBtn} disabled={submitting || !customAmount} onClick={() => submitCredit(null)}>
                  {t("admin.creditCustomSubmit")}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyTarget} onOpenChange={(open) => !open && setHistoryTarget(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid={ADMIN.historyDialog}>
          {historyTarget && (
            <>
              <DialogHeader>
                <DialogTitle>{historyTarget.email}</DialogTitle>
              </DialogHeader>
              {!history ? (
                <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
              ) : (
                <div className="space-y-5 text-sm">
                  <div>
                    <h4 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">{t("admin.transactionsHistory")}</h4>
                    <div className="space-y-1.5 rounded-xl border border-border p-3">
                      {history.transactions.length === 0 && <p className="text-xs text-muted-foreground">-</p>}
                      {history.transactions.map((tx) => (
                        <div key={tx.tx_id} className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">
                            {new Date(tx.created_at).toLocaleString()} · {tx.type}
                            {tx.reason ? ` (${tx.reason})` : ""}
                          </span>
                          <span className={`font-semibold ${NEGATIVE_TX_TYPES.has(tx.type) ? "text-destructive" : "text-success"}`}>
                            {NEGATIVE_TX_TYPES.has(tx.type) ? "-" : "+"}{Math.abs(tx.amount_tokens)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">{t("admin.ordersHistory")}</h4>
                    <div className="space-y-1.5 rounded-xl border border-border p-3">
                      {history.orders.length === 0 && <p className="text-xs text-muted-foreground">-</p>}
                      {history.orders.map((o) => (
                        <div key={o.order_id} className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">{new Date(o.created_at).toLocaleDateString()} · {o.order_id}</span>
                          <span className="flex items-center gap-2">
                            <Badge variant="outline" className="rounded-full text-[10px]">{o.status}</Badge>
                            <span className="font-semibold">{o.pricing.total}€</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
